import { App } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { BodyOf, Result } from "@/shared/api";
import type { paths } from "@/api/types";
import { apiPost, apiUpload, errorMessage, qk } from "@/shared/api";

export type PersonaCommentBody = BodyOf<paths["/api/v1/admin/comments/persona"]["post"]>;
export type PersonaAvatar = Result<
  paths["/api/v1/admin/comments/persona-avatar"]["post"]
>;

/**
 * Отзыв от лица вымышленного участника (доработки §6.2, §7 «Comments»).
 *
 * Две мутации одним хуком: аватарка грузится отдельной ручкой, потому что в
 * момент выбора картинки отзыва ещё нет, — но обе принадлежат одной форме, и
 * разносить их по хукам значило бы связывать их на месте вызова.
 *
 * Зона инвалидации шире, чем кажется. Очередь — очевидно. Журнал — потому что
 * создание синтетики пишется в него отдельным действием, и это единственное
 * доказательство, что строка изготовлена. Сводка — потому что бейдж раздела
 * считает отзывы, ждущие решения.
 *
 * Ошибки тостятся здесь, успех — на месте вызова: текст успеха у загрузки и у
 * создания разный, а текст ошибки приходит с бэкенда и одинаков для обеих.
 */
export function useCreatePersonaComment() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const onError = (error: unknown) => {
    void message.error(errorMessage(error));
  };

  const create = useMutation({
    mutationFn: (body: PersonaCommentBody) =>
      apiPost("/api/v1/admin/comments/persona", { body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["comments"] });
      void queryClient.invalidateQueries({ queryKey: qk.dashboard() });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError,
  });

  const uploadAvatar = useMutation({
    mutationFn: (file: File) =>
      apiUpload<PersonaAvatar>("/api/v1/admin/comments/persona-avatar", { file }),
    onError,
  });

  return { create, uploadAvatar };
}
